// =============================================================================
// SIDEBAR ITEMS — Shared metadata for every nav entry
// =============================================================================
// PURPOSE: Single source of truth for sidebar NavItem metadata (path, label, icon
// key, CATEGORY, search ALIASES). Consumed by:
//   - components/Sidebar.tsx           — the real sidebar (category nav + search)
//   - pages/SidebarViewerPage.tsx      — the dev-tools all-roles preview page
//   - utils/navSearch.ts               — the RBAC-gated synonym search matcher
//
// SCOPE: Desktop sidebar only. MobileBottomNav has its own short tab list and
// is intentionally NOT a consumer of this file.
//
// CATEGORY TAXONOMY (added Jul 2026): every item belongs to exactly one category.
// ONE taxonomy drives BOTH surfaces — the exec/manager collapsible category nav
// AND the rep flat-list sub-group labels. Category membership + order is the SSOT
// for grouping; FLAT_LAYOUT_ORDER / SECTIONED_LAYOUT still define per-role
// membership + intra-group order (and back the drift-detector test).
//
// LABEL OVERRIDES (handled by callers, NOT here):
//   - "/admin/rep-portal" → "{firstName}'s Portal" when hasTunnelVision === true
//   - "/admin/portal"     → "Portal" in flat layout, "Client Portal" sectioned
// These two are runtime conditionals, so callers compute the label themselves.
// The labels here are the DEFAULTS used everywhere else.
// =============================================================================

import { NavIcons } from "../components/ui/Icons";

export type IconKey = keyof typeof NavIcons;

// ─────────────────────────────────────────────────────────────────────────────
// Category taxonomy — render order = array order. Drives the exec/manager
// collapsible category nav headers AND the rep sub-group labels.
// ─────────────────────────────────────────────────────────────────────────────
export type CategoryKey = "home" | "deals" | "analytics" | "comms" | "directory" | "admin";

interface SidebarCategoryMeta {
  key: CategoryKey;
  label: string;
  iconKey: IconKey;
}

export const CATEGORIES: SidebarCategoryMeta[] = [
  { key: "home", label: "Home", iconKey: "Dashboard" },
  { key: "deals", label: "Deals", iconKey: "Transactions" },
  { key: "analytics", label: "Analytics", iconKey: "Analytics" },
  { key: "comms", label: "Comms", iconKey: "Messages" },
  { key: "directory", label: "Directory", iconKey: "Users" },
  { key: "admin", label: "Admin", iconKey: "Background" },
];

export const CATEGORY_ORDER: CategoryKey[] = CATEGORIES.map((c) => c.key);

export interface SidebarItemMeta {
  path: string;
  label: string;
  iconKey: IconKey;
  // Which category group this item renders under (exec nav + rep sub-labels).
  category: CategoryKey;
  // Search synonyms — typing any of these (fuzzy + typo-tolerant, see
  // utils/navSearch.ts) surfaces this item. The label itself is always matched
  // too, so aliases are only the EXTRA nicknames (e.g. "triage" for Lost Leads).
  aliases: string[];
  // devOnly items only render when devMode is on (double-click "Altara" logo).
  // These were the old SECTIONED_LAYOUT.devTools set — WIP/dev pages hidden from
  // Nathaniel's normal view. Search still respects this (hidden unless devMode).
  devOnly?: boolean;
  // ownerOnly items render ONLY for the owner login (OWNER_EMAIL), checked on
  // realUser so it survives View As. Tighter than exec: Nathaniel does not see it.
  // RouteGuard in App.tsx enforces the same rule on direct URL access.
  ownerOnly?: boolean;
}

// The owner = Mako. His Altara login is admin@dynamisrev.com (NOT mako@ — that
// is his Google identity; Altara/Supabase super-admin stayed on admin@).
export const OWNER_EMAIL = "admin@dynamisrev.com";

export const isOwner = (email: string | null | undefined): boolean =>
  (email ?? "").toLowerCase() === OWNER_EMAIL;

// Paths gated to the owner. Derived from ownerOnly so there is one source.
export const isOwnerOnlyPath = (pathname: string): boolean =>
  Object.values(SIDEBAR_ITEMS).some(
    (m) => m.ownerOnly && (pathname === m.path || pathname.startsWith(m.path + "/"))
  );

// Item metadata, keyed by path. Order does NOT matter here — render order is
// controlled by CATEGORY_ORDER + FLAT_LAYOUT_ORDER / SECTIONED_LAYOUT below.
export const SIDEBAR_ITEMS: Record<string, SidebarItemMeta> = {
  "/admin/dashboard": {
    path: "/admin/dashboard",
    label: "Dashboard",
    iconKey: "Dashboard",
    category: "home",
    aliases: ["home", "overview", "main", "kpis", "metrics", "start"],
  },
  "/admin/portal": {
    path: "/admin/portal",
    label: "Client Portal",
    iconKey: "Clients",
    category: "home",
    aliases: ["client", "clients", "customer view", "external"],
  },
  "/admin/rep-portal": {
    path: "/admin/rep-portal",
    label: "Rep Portal",
    iconKey: "User",
    category: "home",
    aliases: ["rep", "agent", "closer portal", "setter portal", "my portal"],
  },
  "/admin/triage": {
    path: "/admin/triage",
    label: "Lost Leads",
    iconKey: "Recover", // person + re-engage arrow — distinct from Call Reviews' sparkles
    category: "deals",
    aliases: [
      "triage",
      "lost lead",
      "lead sheet",
      "dead leads",
      "cold leads",
      "recover",
      "re-engage",
      "reengage",
      "abandoned",
      "revive",
    ],
  },
  "/admin/transactions": {
    path: "/admin/transactions",
    label: "Transactions",
    iconKey: "Transactions",
    category: "deals",
    aliases: ["deals", "sales", "payments", "revenue", "cash", "closed", "tx", "orders"],
  },
  "/admin/installments": {
    path: "/admin/installments",
    label: "Installments",
    iconKey: "Installments",
    category: "deals",
    aliases: ["payment plan", "installment", "recurring", "plans", "paying installment", "pif"],
  },
  "/admin/setter-timelines": {
    path: "/admin/setter-timelines",
    label: "Pipelines",
    iconKey: "Clipboard",
    category: "deals",
    aliases: ["pipeline", "setter timelines", "timelines", "funnel", "stages", "journey"],
  },
  "/admin/calendar": {
    path: "/admin/calendar",
    label: "Calendar",
    iconKey: "Calendar",
    category: "deals",
    aliases: ["schedule", "bookings", "appointments", "calls", "agenda", "meetings"],
  },
  "/admin/leaderboard": {
    path: "/admin/leaderboard",
    label: "Leaderboard",
    iconKey: "Leaderboard",
    category: "analytics",
    aliases: ["rankings", "ranks", "standings", "top reps", "scoreboard", "competition"],
  },
  "/admin/dm-analytics": {
    path: "/admin/dm-analytics",
    label: "DM Analytics",
    iconKey: "Analytics", // line chart — the messaging trend
    category: "analytics",
    aliases: ["dm", "dms", "direct messages", "setter analytics", "messaging stats", "setter"],
  },
  "/admin/dial-analytics": {
    path: "/admin/dial-analytics",
    label: "Dial Analytics",
    iconKey: "ChartBar", // bar chart — distinct from DM Analytics' line chart
    category: "analytics",
    aliases: ["dial", "dialer", "dials", "call analytics", "phone analytics", "dialing"],
  },
  "/admin/stoplights": {
    path: "/admin/stoplights",
    label: "Stoplights",
    iconKey: "Heatmap", // grid — matches the R/Y/G week-grid the page renders
    category: "analytics",
    aliases: [
      "stoplight",
      "red yellow green",
      "health grid",
      "status grid",
      "wow",
      "week over week",
    ],
  },
  "/admin/heatmap": {
    path: "/admin/heatmap",
    label: "Webinar Analytics",
    iconKey: "ChartBar",
    category: "analytics",
    aliases: ["webinar", "webinars", "heatmap", "attendance", "zoom", "deep dive"],
    devOnly: true,
  },
  "/admin/mastermind": {
    path: "/admin/mastermind",
    label: "Exec Control Center",
    iconKey: "ChartBar",
    category: "analytics",
    aliases: ["mastermind", "control center", "command center", "exec", "cockpit"],
    devOnly: true,
  },
  "/admin/instagram": {
    path: "/admin/instagram",
    label: "Instagram DMs",
    iconKey: "Messages",
    category: "comms",
    aliases: ["instagram", "ig", "inbox", "dm inbox", "social", "manychat"],
  },
  "/admin/call-reviews": {
    path: "/admin/call-reviews",
    label: "Call Reviews",
    iconKey: "Sparkles", // AI-graded reviews — sparkles now unique to this item
    category: "comms",
    aliases: ["reviews", "graded calls", "ai review", "scorecards", "feedback", "call grading"],
  },
  "/admin/call-search": {
    path: "/admin/call-search",
    label: "Call Search",
    iconKey: "Phone",
    category: "comms",
    aliases: ["find call", "search calls", "transcripts", "recordings", "call lookup", "fathom"],
  },
  "/admin/precall-compliance": {
    path: "/admin/precall-compliance",
    label: "Pre-Call Compliance",
    iconKey: "Compliance", // shield+check — frees Clipboard for Pipelines
    category: "comms",
    aliases: ["precall", "pre-call", "compliance", "precall form", "qualification"],
    devOnly: true,
  },
  "/admin/lead-search": {
    path: "/admin/lead-search",
    label: "Lead Search",
    iconKey: "Search",
    category: "directory",
    aliases: ["find lead", "search leads", "lookup lead", "contacts", "prospects"],
  },
  "/admin/lead-matches": {
    path: "/admin/lead-matches",
    label: "Lead Matches",
    iconKey: "Users",
    category: "directory",
    aliases: ["matches", "matching", "dedupe", "duplicates", "merge leads"],
    devOnly: true,
  },
  "/admin/workforce": {
    path: "/admin/workforce",
    label: "Workforce",
    iconKey: "Users",
    category: "directory",
    aliases: ["roster", "people", "team", "reps", "onboarding", "workforce", "hiring"],
  },
  "/admin/sop-library": {
    path: "/admin/sop-library",
    label: "SOP Library",
    iconKey: "Docs", // book icon — distinct from Call Reviews' sparkles
    category: "directory",
    aliases: ["sop", "sops", "docs", "documentation", "playbook", "guides", "how to", "procedures"],
  },
  "/admin/course": {
    path: "/admin/course",
    label: "Course",
    iconKey: "Docs",
    category: "directory",
    aliases: ["course", "training", "lessons", "phone setter course", "closer course", "script"],
  },
  "/admin/quiz": {
    path: "/admin/quiz",
    label: "Quiz",
    iconKey: "Trophy",
    category: "directory",
    aliases: [
      "quiz",
      "daily quiz",
      "course quiz",
      "test",
      "assessment",
      "questions",
      "quiz stats",
      "quiz scores",
    ],
  },
  "/admin/handbook": {
    path: "/admin/handbook",
    label: "Handbook",
    iconKey: "Docs",
    category: "directory",
    aliases: [
      "handbook",
      "dynamis handbook",
      "values",
      "manifesto",
      "standards",
      "culture",
      "start here",
    ],
  },
  "/admin/welcome": {
    path: "/admin/welcome",
    label: "Welcome Video",
    iconKey: "Docs",
    category: "directory",
    aliases: ["welcome", "welcome video", "onboarding video"],
    devOnly: true,
  },
  "/admin/winners": {
    path: "/admin/winners",
    label: "Winners",
    iconKey: "Trophy",
    category: "directory",
    aliases: ["prestige", "awards", "achievements", "crowns", "hall of fame"],
    devOnly: true,
  },
  "/admin/invoice": {
    path: "/admin/invoice",
    label: "Invoice",
    iconKey: "Invoice",
    category: "admin",
    aliases: ["invoices", "billing", "receipts"],
    devOnly: true,
  },
  "/admin/registration-links": {
    path: "/admin/registration-links",
    label: "Registration Links",
    iconKey: "Link",
    category: "admin",
    aliases: ["registration", "register", "signup links", "reg links", "enrollment"],
    devOnly: true,
  },
  "/admin/calendly": {
    path: "/admin/calendly",
    label: "Calendly Heatmap",
    iconKey: "Heatmap",
    category: "admin",
    aliases: ["calendly", "booking heatmap", "availability"],
    devOnly: true,
  },
  "/admin/background": {
    path: "/admin/background",
    label: "Background",
    iconKey: "Background",
    category: "admin",
    aliases: ["settings", "config", "theme", "appearance"],
    devOnly: true,
  },
  "/admin/sidebar-viewer": {
    path: "/admin/sidebar-viewer",
    label: "Sidebar Viewer",
    iconKey: "Eye",
    category: "admin",
    aliases: ["sidebar", "rbac viewer", "role viewer", "nav viewer", "preview"],
    devOnly: true,
  },
};

// FLAT layout — used for tunnel-vision roles (closer/setter/dialer) and client.
// Portal is rendered first only when the role has it AND is NOT exec
// (exec gets it in the Dashboards section instead — handled by caller).
// NOTE: Dial HQ removed from the sidebar entirely (Mako, Jul 2026) — dialers now
// land on Rep Portal (roleConfig.dialer.pages[0]).
export const FLAT_LAYOUT_ORDER: string[] = [
  "/admin/portal", // client only (caller filters)
  "/admin/rep-portal",
  "/admin/handbook", // Dynamis Handbook — every role reads it first, so it sits high (HB-END A, 2026-10-08)
  "/admin/quiz", // Daily course quiz — first try of the day scores (REP_ONBOARDING, 2026-10-08)
  "/admin/triage", // Lost Leads — surfaced high for reps (closer/setter/dialer)
  "/admin/transactions",
  "/admin/installments",
  "/admin/leaderboard",
  "/admin/calendar",
  "/admin/dm-analytics",
  "/admin/course", // Course reader — roleConfig let reps open it since 2026-10-06, but it was never in this list, so no sidebar link (Norman, 2026-10-25)
  "/admin/dial-analytics", // closer + dialer only (gated by roleConfig.pages)
  "/admin/instagram",
  "/admin/setter-timelines",
  "/admin/lead-search",
  "/admin/call-search",
  "/admin/call-reviews",
  "/admin/sop-library", // SOP Library — visible to all roles, content gated server-side
];

// SECTIONED layout — used for manager and exec.
export const SECTIONED_LAYOUT = {
  dashboards: ["/admin/dashboard", "/admin/portal", "/admin/rep-portal"],
  management: [
    "/admin/triage", // Lost Leads — moved up from Dev Tools for prominence
    "/admin/workforce", // People console — roster, onboarding, coaching, offboarding
    "/admin/transactions",
    "/admin/installments",
    "/admin/leaderboard",
    "/admin/calendar",
    "/admin/dm-analytics",
    "/admin/dial-analytics", // exec-only here (gated by roleConfig.pages — manager excludes it)
    "/admin/instagram",
    "/admin/setter-timelines",
    "/admin/lead-search",
    "/admin/call-search",
    "/admin/call-reviews",
    "/admin/stoplights",
    "/admin/sop-library", // SOP Library — visible to all roles, content gated server-side
    "/admin/course", // Course reader — reps, managers, execs (owner gate lifted 2026-10-06)
    "/admin/handbook", // Dynamis Handbook — every role reads it first (HB-END A, 2026-10-08)
    "/admin/quiz", // Daily course quiz (REP_ONBOARDING, 2026-10-08)
  ],
  devTools: [
    "/admin/mastermind",
    "/admin/precall-compliance",
    "/admin/winners",
    "/admin/lead-matches",
    "/admin/heatmap",
    "/admin/calendly",
    "/admin/invoice",
    "/admin/registration-links",
    "/admin/background",
    "/admin/sidebar-viewer",
    "/admin/welcome", // Welcome video — devOnly, reps open it by URL
  ],
};
